import React from 'react';
import { XIcon, AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react';

const CaveatsModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700 p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <AlertTriangleIcon className="h-6 w-6 text-yellow-400" />
            <h2 className="text-2xl font-black text-white">Important Caveats & Guidelines</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg glass border border-slate-600/30 text-slate-400 hover:text-white hover:border-slate-500/50 transition-all"
            title="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* AI Responsibility */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              AI Hallucinations & Accuracy
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              NeuroForge is not responsible for AI-generated content inaccuracies or hallucinations.
              All three AI models (GPT, Claude, and Gemini) may occasionally generate incorrect, misleading,
              or fabricated information. Always verify critical information from authoritative sources.
              Use this platform for entertainment, exploration, and comparative analysis purposes.
            </p>
          </div>

          {/* Language Consistency */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Language Consistency (Coming in v2.0)
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-3">
              <strong className="text-yellow-300">Current Limitation:</strong> Keep your conversation in a
              single language throughout the entire thread. Mixing languages (e.g., starting in English,
              then switching to Romanian, then Hungarian) can cause unexpected AI behavior, incomplete
              responses, or errors.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed">
              <strong className="text-green-300">Recommended:</strong> Use one language per thread for
              optimal results. Multi-language support will be enhanced in version 2.0.
            </p>
          </div>

          {/* Prompt Consumption & Limits */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Prompt Limits & Consumption Policy
            </h3>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Each AI agent has token limits in place to ensure fair usage and system stability.
                Understanding how prompts are consumed is crucial:
              </p>

              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                <p className="font-semibold text-yellow-300 mb-2">Partial Success Scenarios:</p>
                <p>
                  If <strong>2 out of 3</strong> agents respond successfully while one fails due to
                  exceeding token limits (e.g., GPT and Claude succeed, but Gemini errors out),
                  <strong className="text-red-300"> your prompt WILL be consumed</strong>. Prompts are
                  NOT refunded for partial failures since the majority of agents provided valid responses.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                <p className="font-semibold text-green-300 mb-2">Full Failure Scenarios:</p>
                <p>
                  Prompts are <strong>refunded or not consumed</strong> only when <strong>all 3 agents fail</strong>
                  to respond due to system errors, network issues, or other critical failures. This ensures
                  you're only charged for successful battle rounds.
                </p>
              </div>

              <p className="text-slate-400 italic mt-3">
                💡 <strong>Tip:</strong> Keep your prompts concise and within reasonable length to avoid
                hitting token limits on any single agent.
              </p>
            </div>
          </div>

          {/* Billing Issues */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Incorrect Prompt Consumption
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              If you believe prompts were consumed incorrectly or you experienced unexpected behavior
              that shouldn't have counted against your quota, please reach out to us at{' '}
              <a
                href="mailto:contact@web-runner.net"
                className="text-cyan-400 hover:text-cyan-300 underline font-medium"
              >
                contact@web-runner.net
              </a>
              . We'll review your case and can manually top up your prompt balance if warranted.
            </p>
          </div>

          {/* Bug Reports */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Bug Reports & Quality Assurance
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              NeuroForge v1.0 is feature-complete and production-ready. However, no software is entirely
              bug-free. If you encounter any issues, unexpected behavior, or have suggestions for improvement,
              please report them to{' '}
              <a
                href="mailto:contact@web-runner.net"
                className="text-cyan-400 hover:text-cyan-300 underline font-medium"
              >
                contact@web-runner.net
              </a>
              . Your feedback helps us continuously improve the platform.
            </p>
          </div>

          {/* Standalone Version */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Need Higher Limits?
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              If the current token limits or prompt quotas don't meet your needs, consider the{' '}
              <strong className="text-white">standalone self-hosted version</strong> of NeuroForge.
              The standalone version offers unlimited prompts, customizable limits, and full control
              over your AI battle arena. Contact us for licensing and deployment options.
            </p>
          </div>

          {/* Ongoing Development */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Continuous Development
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              NeuroForge is under active development. We're committed to regularly releasing updates,
              bug fixes, and exciting new features. Stay tuned for v2.0 which will include enhanced
              multi-language support, advanced analytics, custom scoring algorithms, and more!
            </p>
          </div>

          {/* Additional Considerations */}
          <div className="glass border border-slate-600/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              Additional Considerations
            </h3>
            <ul className="space-y-2 text-slate-300 text-sm leading-relaxed list-disc list-inside">
              <li>
                <strong>Response Times:</strong> AI response times vary based on model availability,
                server load, and prompt complexity. Patience is appreciated during high-traffic periods.
              </li>
              <li>
                <strong>Data Privacy:</strong> Your prompts and conversations are stored securely.
                We do not share your data with third parties. Review our Privacy Policy for details.
              </li>
              <li>
                <strong>Fair Use:</strong> Please use NeuroForge responsibly. Abusive behavior, spam,
                or attempts to exploit the system may result in account suspension.
              </li>
              <li>
                <strong>Analysis Feature:</strong> The analysis feature allows you to have remaining
                agents critique responses. Use it strategically to eliminate weaker answers and find
                the ultimate winner.
              </li>
            </ul>
          </div>

          {/* Terms & Conditions */}
          <div className="glass border border-yellow-500/30 rounded-xl p-5 bg-yellow-500/5">
            <h3 className="text-lg font-bold text-yellow-300 mb-3 flex items-center gap-2">
              <AlertTriangleIcon className="h-5 w-5" />
              Stay Informed
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-3">
              <strong>Important:</strong> These caveats may be updated at any time to reflect new
              features, policy changes, or service improvements. We recommend reviewing this section
              regularly to stay informed about the latest guidelines.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed">
              All caveats and policies are also covered in our{' '}
              <a
                href="https://web-runner.net/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline font-medium inline-flex items-center gap-1"
              >
                Terms & Conditions
                <ExternalLinkIcon className="h-3 w-3" />
              </a>
              . By using NeuroForge, you agree to abide by these terms.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800/95 backdrop-blur-sm border-t border-slate-700 p-6">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 px-6 rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-cyan-500/50"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaveatsModal;
